import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid2,
  Typography,
  Box,
  IconButton,
  Button,
  useTheme,
} from '@mui/material';
import { debounce, cloneDeep } from 'lodash';
import RefreshIcon from '@mui/icons-material/Refresh';

import ReactMuiTableListView from '../Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../Common/ReactMuiTableColumnHeaderTextEllipsis';
import SearchBox from '../Common/SearchBox';
import DateRangePicker from '../Common/DateRangePicker';
import AccessLogsCharts from './AccessLogCharts';
import useCommon from '../../hooks/useCommon';
import useOrganization from '../../hooks/UseOrganization';
import { ReactComponent as DownloadIcon } from '../../assets/DownloadIcon.svg';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import { CommonTableCell, DateTimeCell } from '../Common/CommonTableCell';
import {
  DropdownItem,
  TransparentDropdown,
} from '../Common/TransparentDropdown';

const tableColumns = [
  {
    heading: 'USERNAME',
    accessor: 'user_name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 110,
    Cell: CommonTableCell,
  },
  {
    heading: 'USER ID',
    accessor: 'user_id',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 80,
    Cell: CommonTableCell,
  },
  {
    heading: 'TIMESTAMP',
    accessor: '@timestamp',
    Cell: DateTimeCell,
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 150,
  },
  {
    heading: 'IP',
    accessor: 'ip_address',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 100,
    Cell: CommonTableCell,
  },
  {
    heading: 'REQUEST TYPE',
    accessor: 'request_type',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 110,
    Cell: CommonTableCell,
  },
  {
    heading: 'RESOURCE',
    accessor: 'resource_name',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 100,
    Cell: CommonTableCell,
  },
  {
    heading: 'PRODUCT',
    accessor: 'product',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 100,
    Cell: CommonTableCell,
  },
  {
    heading: 'MESSAGE',
    accessor: 'message',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 250,
    Cell: CommonTableCell,
  },
  {
    heading: 'CATEGORY',
    accessor: 'category',
    Header: ReactMuiTableColumnHeaderTextEllipsis,
    width: 100,
    Cell: CommonTableCell,
  },
];

const All_Products_Name = 'All Products';

const AccessLogs = ({
  fetchAccessLogs,
  fetchUserStats,
  accessLogsList,
  accessLogsPageCount,
  totalAccessLogsCount,
  orgName,
  DownloadLogs,
}) => {
  const theme = useTheme();
  const {
    dispatch,
    common,
    setAccessLogsCreatedOnFilter,
    setAccessLogsNameSearch,
  } = useCommon();

  const { getProductList } = useOrganization();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(5);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [loadCharts, setLoadCharts] = useState(false);
  const [userStats, setUserStats] = useState({});
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('all_products');

  const getProductParam = useCallback(
    (productId) => {
      const name = productList.find((p) => p.id === productId)?.name;
      return name === All_Products_Name ? '' : (name ?? '');
    },
    [productList]
  );

  const downloadLogs = useCallback(() => {
    const productParam = getProductParam(selectedProduct);
    const parameters = {
      type: 'access',
      start_date: common.accessLogsCreatedOnFilter?.fromDate,
      end_date: common.accessLogsCreatedOnFilter?.toDate,
      text: common.accessLogsNameSearch || '',
      product: productParam,
    };
    DownloadLogs(orgName, parameters).then((res) => {
      window.open(res.data, '_blank', 'noopener', 'noreferrer');
    });
  }, [
    orgName,
    common.accessLogsCreatedOnFilter,
    common.accessLogsNameSearch,
    DownloadLogs,
    selectedProduct,
  ]);

  const fetchAccessLogsListWrapper = useCallback(() => {
    if (currentPageIndex >= 0 && currentPageSize) {
      const productParam = getProductParam(selectedProduct);
      fetchAccessLogs(
        currentPageIndex,
        currentPageSize,
        common.accessLogsNameSearch,
        common.accessLogsCreatedOnFilter?.fromDate,
        common.accessLogsCreatedOnFilter?.toDate,
        productParam,
        sortField,
        sortOrder,
        orgName
      );
    }
  }, [
    common.accessLogsNameSearch,
    common.accessLogsCreatedOnFilter?.fromDate,
    common.accessLogsCreatedOnFilter?.toDate,
    currentPageIndex,
    currentPageSize,
    sortField,
    sortOrder,
    selectedProduct,
  ]);

  const handleSearch = useCallback(
    debounce(
      (
        globalFilter,
        pageIndex,
        pageSize,
        sortfield,
        sortorder,
        fromDate,
        toDate,
        product
      ) => {
        fetchAccessLogs(
          pageIndex,
          pageSize,
          globalFilter,
          fromDate,
          toDate,
          product,
          sortfield,
          sortorder,
          orgName
        );
      },
      1200
    ),
    [fetchAccessLogs]
  );

  const cancelDebouncedCall = () => {
    return handleSearch.cancel();
  };

  useEffect(() => {
    if (currentPageIndex >= 0 && currentPageSize) {
      if (common.accessLogsNameSearch.length > 0) {
        const productParam = getProductParam(selectedProduct);
        handleSearch(
          common.accessLogsNameSearch,
          currentPageIndex,
          currentPageSize,
          sortField,
          sortOrder,
          common.accessLogsCreatedOnFilter?.fromDate,
          common.accessLogsCreatedOnFilter?.toDate,
          productParam
        );
      } else {
        cancelDebouncedCall();
        fetchAccessLogs(
          currentPageIndex,
          currentPageSize,
          common.accessLogsNameSearch,
          common.accessLogsCreatedOnFilter?.fromDate,
          common.accessLogsCreatedOnFilter?.toDate,
          getProductParam(selectedProduct),
          sortField,
          sortOrder,
          orgName
        );
      }
    }
  }, [
    currentPageIndex,
    currentPageSize,
    common.accessLogsNameSearch,
    common.accessLogsCreatedOnFilter?.fromDate,
    common.accessLogsCreatedOnFilter?.toDate,
    sortField,
    sortOrder,
    orgName,
    selectedProduct,
  ]);

  const handleSortChange = useCallback(
    (sortDetails) => {
      if (sortDetails.length) {
        const [{ id, desc }] = sortDetails;
        setSortOrder(desc ? 'desc' : 'asc');
        setSortField(id);
      } else {
        setSortOrder('');
        setSortField('');
      }
    },
    [setSortField, setSortOrder]
  );

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setCurrentPageIndex(pageIndexData?.pageIndex);
      setCurrentPageSize(pageIndexData?.pageSize);
    },
    [setCurrentPageIndex, setCurrentPageSize]
  );

  const handleLoadCharts = useCallback(() => {
    setLoadCharts(true);
    fetchUserStats(orgName).then(
      (res) => setUserStats(res?.data),
      () => {}
    );
  }, [orgName]);

  const handleDateRangePickerOnChange = useCallback(
    (fromDate, toDate) => {
      // eslint-disable-next-line no-underscore-dangle
      if (fromDate?._isValid && toDate?._isValid) {
        const clonedFromDate = cloneDeep(fromDate);
        const clonedToDate = cloneDeep(toDate);
        dispatch(
          setAccessLogsCreatedOnFilter({
            fromDate: clonedFromDate.format('YYYY/MM/DD'),
            toDate: clonedToDate.format('YYYY/MM/DD'),
          })
        );
      }
      if (fromDate === null && toDate === null) {
        dispatch(
          setAccessLogsCreatedOnFilter({
            fromDate: null,
            toDate: null,
          })
        );
      }
    },
    [setAccessLogsCreatedOnFilter, dispatch]
  );

  const handleAccessLogsNameSearch = useCallback(
    (e) => {
      const { value } = e.target;
      setSearchTerm(value);
      if (value) {
        dispatch(setAccessLogsNameSearch(value));
      } else {
        dispatch(setAccessLogsNameSearch(''));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (orgName) {
      getProductList(orgName).then((res) => {
        const adminPanelProduct = {
          id: 'admin_panel',
          name: 'Admin Panel',
        };
        const allProducts = { id: 'all_products', name: All_Products_Name };
        setProductList([allProducts, adminPanelProduct, ...(res?.data || [])]);
      });
    }
  }, [orgName]);

  const handleProductChange = useCallback((event) => {
    const newValue = event.target.value;
    window.dispatchEvent(new CustomEvent('RESET_PAGE_INDEX'));
    setSelectedProduct(newValue);
    setCurrentPageIndex(0);
  }, []);

  return (
    <Grid2 container spacing={2} mt={2} mb={1}>
      {loadCharts === true && (
        <Grid2 size={{ xs: 12 }}>
          <AccessLogsCharts
            userStats={userStats}
            fetchUserStats={fetchUserStats}
          />
        </Grid2>
      )}
      <Grid2 size={{ xs: 12 }}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <IconButton
              id="refresh-btn"
              color="primary"
              onClick={fetchAccessLogsListWrapper}
              aria-label="Refresh"
              title="Refresh access logs"
            >
              <RefreshIcon color="primary" />
            </IconButton>

            <Box width="200px" ml={1}>
              <SearchBox
                value={common.accessLogsNameSearch}
                onChange={handleAccessLogsNameSearch}
                placeholder="Search"
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box width={350}>
              <DateRangePicker
                format="DD/MM/YYYY"
                initialFromDate={common?.accessLogsCreatedOnFilter?.fromDate}
                initialToDate={common?.accessLogsCreatedOnFilter?.toDate}
                onChange={handleDateRangePickerOnChange}
                TextFieldProps={{
                  size: 'small',
                  InputProps: {
                    sx: {
                      input: {
                        paddingTop: '15px',
                      },
                    },
                  },
                }}
                isFutureDatesDisabled={true}
              />
            </Box>
            <Box width="200px">
              <TransparentDropdown
                select={true}
                value={selectedProduct}
                onChange={handleProductChange}
              >
                {productList.map((product) => (
                  <DropdownItem key={product.id} value={product.id}>
                    {product.name}
                  </DropdownItem>
                ))}
              </TransparentDropdown>
            </Box>
            <IconButton
              title="Download CSV"
              color="primary"
              onClick={downloadLogs}
            >
              <DownloadIcon />
            </IconButton>
            {!loadCharts && (
              <Box>
                <Button
                  title="Load Charts"
                  variant="contained"
                  onClick={handleLoadCharts}
                  color="primary"
                >
                  Show Stats
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        {accessLogsList?.length > 0 ? (
          <ReactMuiTableListView
            data={accessLogsList}
            columns={tableColumns}
            onTableStateChange={onTableStateChangeMethod}
            getHeaderProps={() => ({
              style: { display: 'flex', alignItems: 'center' },
            })}
            getRowProps={() => ({
              style: { position: 'relative' },
            })}
            enableRowSelection={true}
            pageCount={accessLogsPageCount}
            dataLength={Number(totalAccessLogsCount)}
            enablePagination={true}
            initialPageSize={5}
            rowsPerPageOptions={[5, 10, 15, 20]}
            rowLevelRightClickMenus={[]}
            enableManualFilter={true}
            enableControlledPagination={true}
            showPaginationAction={false}
            skipPageReset={true}
            searchTerm={searchTerm}
            enableManualSort
            onSortChange={handleSortChange}
          />
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            overflow="auto"
            minHeight="300px"
            height="calc(100vh - 438px)"
          >
            {searchTerm ? (
              <NoDataImage style={{ color: theme.palette.primary.main }} />
            ) : (
              <NoDataCreated style={{ color: theme.palette.primary.main }} />
            )}
            <Typography
              variant="subtitle1"
              textAlign="center"
              sx={{ pt: '10px' }}
            >
              {searchTerm
                ? 'No matching result found.'
                : 'No access logs generated.'}
            </Typography>
          </Box>
        )}
      </Grid2>
    </Grid2>
  );
};

export default AccessLogs;
